export type LocalFaq = { question: string; answer: string };

export type LocalCitySeo = {
  city: string;
  slug: string;
  service: 'internet' | 'phone';
  headline: string;
  intro: string[];
  areas: string[];
  whyLocal: string[];
  faqs: LocalFaq[];
  relatedLinks: { label: string; href: string }[];
};

export const KINGMAN_INTERNET: LocalCitySeo = {
  city: 'Kingman',
  slug: 'internet-kingman-az',
  service: 'internet',
  headline: 'High-speed internet for Kingman homes and businesses',
  intro: [
    'Airmax Internet is a local ISP serving Kingman, Arizona with WAVE Fiber Speeds where available. Whether you live along Stockton Hill Road, near the airport corridor, or in surrounding neighborhoods, we focus on reliable everyday speeds for streaming, remote work, and business connectivity.',
    'Unlike national providers that treat Mohave County as an afterthought, Airmax supports customers from Arizona with local technicians, clear pricing, and free installation on featured plans. Check your Kingman address for WAVE availability, then choose STARTER or a higher WAVE package that fits your home or office.',
  ],
  areas: [
    'Stockton Hill Road and northern Kingman neighborhoods',
    'Airway Avenue / Kingman Airport area where coverage allows',
    'Nearby Mohave County addresses served from our Kingman footprint',
  ],
  whyLocal: [
    'Local Arizona support at 928-565-6707 — real people who know the Kingman network',
    'Unlimited data on featured plans with transparent monthly pricing',
    'Professional installation and equipment options for homes and businesses',
  ],
  faqs: [
    {
      question: 'Does Airmax offer internet in Kingman, AZ?',
      answer:
        'Yes. Airmax provides high-speed WAVE Fiber Speeds internet (where available) to homes and businesses in Kingman, Arizona. Use Check Service to confirm your exact address.',
    },
    {
      question: 'How do I know which speeds are available at my Kingman address?',
      answer:
        'Coverage varies by location. Submit your Kingman address on our Check Service page or call 928-565-6707 and we will confirm WAVE availability and which packages you can order, including STARTER.',
    },
    {
      question: 'Is installation free in Kingman?',
      answer:
        'Featured Airmax internet plans include free installation. Plan details and any optional equipment rentals are listed on this page.',
    },
  ],
  relatedLinks: [
    { label: 'Golden Valley internet', href: '/internet-golden-valley-az' },
    { label: 'Kingman phone service', href: '/phone-kingman-az' },
    { label: 'Check Kingman availability', href: '/' },
    { label: 'All internet plans', href: '/plans' },
  ],
};

export const GOLDEN_VALLEY_INTERNET: LocalCitySeo = {
  city: 'Golden Valley',
  slug: 'internet-golden-valley-az',
  service: 'internet',
  headline: 'Reliable internet built for Golden Valley, Arizona',
  intro: [
    'Golden Valley residents and businesses choose Airmax Internet for local wireless service with WAVE Fiber Speeds, designed for northwestern Arizona. From residential streets across the valley to commercial locations that need stable upload and download performance, we deliver service with Arizona-based support.',
    'Our Golden Valley coverage focuses on practical speeds for households, home offices, and small businesses — with free installation on featured plans and unlimited data options. Confirm availability at your Golden Valley address, then pick the plan that matches how you use the internet every day.',
  ],
  areas: [
    'Central Golden Valley residential areas',
    'Properties along Highway 68 corridor where service is available',
    'Rural and semi-rural Golden Valley locations covered by our wireless network',
    'Nearby addresses in the greater Golden Valley / Mohave County service area',
  ],
  whyLocal: [
    'A true local ISP — not a call center overseas for every outage or install question',
    'Wireless internet with WAVE Fiber Speeds on eligible plans — not a fiber-to-the-home install',
    'Straightforward plans with free installation on featured packages',
    'Same trusted Airmax team serving Golden Valley and Kingman',
  ],
  faqs: [
    {
      question: 'Is Airmax available in Golden Valley, AZ?',
      answer:
        'Yes. Airmax Internet serves Golden Valley, Arizona with wireless internet and WAVE Fiber Speeds where available. Check your address online or call 928-565-6707.',
    },
    {
      question: 'What internet speeds are available in Golden Valley?',
      answer:
        'Available speeds depend on your location. Current wireless plans and WAVE Fiber Speeds options are listed on this page.',
    },
    {
      question: 'How long does installation take in Golden Valley?',
      answer:
        'After you order and we confirm coverage, our local installation team schedules a professional install. Timing depends on schedule and site conditions — call us for current lead times.',
    },
  ],
  relatedLinks: [
    { label: 'Kingman internet', href: '/internet-kingman-az' },
    { label: 'Golden Valley phone service', href: '/phone-golden-valley-az' },
    { label: 'Check Golden Valley availability', href: '/' },
    { label: 'All internet plans', href: '/plans' },
  ],
};

export const KINGMAN_PHONE: LocalCitySeo = {
  city: 'Kingman',
  slug: 'phone-kingman-az',
  service: 'phone',
  headline: 'Home and business phone service in Kingman, AZ',
  intro: [
    'Airmax provides residential and business phone service for Kingman, Arizona customers who want clear local calling, unlimited talk options, and the ability to keep an existing number when porting is available.',
    'Pair phone service with Airmax internet or use it as a standalone voice solution for your Kingman home or office. Our Arizona support team helps with setup, features, and Local TN letter-of-authorization (LOA) porting paperwork.',
  ],
  areas: [
    'Kingman residences needing a reliable home phone line',
    'Kingman small businesses and offices',
    'Customers porting numbers from another Kingman-area provider',
    'Locations already on Airmax internet who want bundled voice',
  ],
  whyLocal: [
    'Local number porting support with guided LOA submission',
    'Home and business feature sets priced for Kingman customers',
    'Arizona-based support at 928-565-6707',
    'Works alongside Airmax internet service in Kingman',
  ],
  faqs: [
    {
      question: 'Can I keep my Kingman phone number with Airmax?',
      answer:
        'In most cases yes — you can request to port your existing number. Complete the Local TN LOA form on this page or call 928-565-6707 for help.',
    },
    {
      question: 'Do I need Airmax internet to get phone service in Kingman?',
      answer:
        'Ask our team about current requirements for your address. Many customers add phone service with their Airmax internet; we will confirm what is available for you.',
    },
    {
      question: 'What phone plans are available in Kingman?',
      answer:
        'Residential and business phone packages are listed on this page, including features such as unlimited calling options and business-ready tools on higher tiers.',
    },
  ],
  relatedLinks: [
    { label: 'Golden Valley phone service', href: '/phone-golden-valley-az' },
    { label: 'Kingman internet', href: '/internet-kingman-az' },
    { label: 'All phone service', href: '/phone-service' },
    { label: 'Contact Airmax', href: '/contact' },
  ],
};

export const GOLDEN_VALLEY_PHONE: LocalCitySeo = {
  city: 'Golden Valley',
  slug: 'phone-golden-valley-az',
  service: 'phone',
  headline: 'Phone service for Golden Valley homes and businesses',
  intro: [
    'Stay connected in Golden Valley, Arizona with Airmax home and business phone service. We offer clear voice service, practical feature packages, and support for porting your existing number when eligible.',
    'Golden Valley customers use Airmax phone service for everyday household calling and for small-business lines that need dependable local support — without relying on a distant national call center for every change.',
  ],
  areas: [
    'Golden Valley residences',
    'Local small businesses and home offices',
    'Customers moving a number from another Golden Valley provider',
    'Airmax internet customers adding voice service',
  ],
  whyLocal: [
    'Number porting assistance with Local TN LOA forms',
    'Residential and business phone plans with clear pricing',
    'Local Arizona support familiar with Golden Valley service',
    'Easy to add alongside Airmax internet in Golden Valley',
  ],
  faqs: [
    {
      question: 'Does Airmax offer phone service in Golden Valley?',
      answer:
        'Yes. Airmax provides home and business phone service in Golden Valley, Arizona, including plan options and number porting support where available.',
    },
    {
      question: 'How do I port my Golden Valley number to Airmax?',
      answer:
        'Use the Local TN LOA form on this page with your account details from your previous provider, or call 928-565-6707 and we will walk you through the process.',
    },
    {
      question: 'Is phone service available without fiber?',
      answer:
        'Phone availability depends on your service path and location. Contact us with your Golden Valley address and we will confirm what voice options we can provide.',
    },
  ],
  relatedLinks: [
    { label: 'Kingman phone service', href: '/phone-kingman-az' },
    { label: 'Golden Valley internet', href: '/internet-golden-valley-az' },
    { label: 'All phone service', href: '/phone-service' },
    { label: 'Contact Airmax', href: '/contact' },
  ],
};
